
import { supabase } from '@/lib/supabase';
import { User, Session } from '@supabase/supabase-js';
import { BaseService } from './baseService';

/**
 * Service for user and authentication-related data operations
 */
export class UserService extends BaseService {
  /**
   * Get the current authenticated user
   */
  static async getCurrentUser(): Promise<User | null> {
    try {
      const { data, error } = await supabase.auth.getUser();
      if (error) throw error;
      return data.user;
    } catch (error) {
      this.handleApiError(error, 'Error fetching current user');
      return null; // This line is never reached due to throw in handleApiError, but needed for TypeScript
    }
  }

  /**
   * Get the current user session
   */
  static async getCurrentSession(): Promise<Session | null> {
    try {
      const { data, error } = await supabase.auth.getSession();
      if (error) throw error;
      return data.session;
    } catch (error) {
      this.handleApiError(error, 'Error fetching current session');
      return null;
    }
  }

  /**
   * Get current user's role from metadata
   */
  static async getUserRole(): Promise<string | null> {
    try {
      const { data, error } = await supabase.auth.getUser();
      if (error) throw error;
      return data.user?.user_metadata?.role || null;
    } catch (error) {
      this.handleApiError(error, 'Error fetching user role');
      return null;
    }
  }

  /**
   * Check if current user is an admin
   */
  static async isUserAdmin(): Promise<boolean> {
    try {
      const role = await this.getUserRole();
      const adminRoles = ['owner', 'bizadmin', 'admin', 'superadmin'];
      return adminRoles.includes(role || '');
    } catch (error) {
      this.handleApiError(error, 'Error checking admin status');
      return false;
    }
  }
}
